import * as React from 'react';

import { ActivityIndicator, Pressable, WBIcon, IconNameType, WBText, View, ViewProps } from '@/core';
import { StyleSheet } from '@/core/StyleSheet';
import { getModifiedStyles } from '@/helpers';
import itemStyles from './style';

/**
 * Items are actionable UI list elements.
 *
 * WBItem components are supposed to be used in a list of vertical items, each of which execute a certain action (like navigation
 *  to a new screen, opening a modal filter, etc).
 *
 * Example usecases:
 * - UI elements on the reservation form to open floor and zone filters, displaying labels, placeholders and the selected filter(s).
 * - Items in a list of navigation elements.
 *
 * @example
 * <WBItem label='User preferences' onPress={() => someNavigationMethod()} />
 *
 * @todo Remove double spaces at the beginning of value, which worked as a short-term solution for chopped text on Android.
 * https://stackoverflow.com/questions/54750503/text-is-getting-cut-off-in-android-for-react-native
 */

export interface ItemProps {
	/**
	 * Determines whether to show a chevron on the right of the component.
	 */
	chevron?: boolean;
	/**
	 * The icon to be displayed at the far left of the component.
	 */
	icon?: IconNameType;
	/**
	 * Determines disabled state for the item. Disables any user interaction when true.
	 */
	isDisabled?: boolean;
	/**
	 * The label to be displayed within the component.
	 */
	label?: string | React.ReactNode;
	/**
	 * Determines loading state for the item, shows a loading indicator. Could be used when the item's action is not initialized yet, e.g. reservation filters.
	 */
	loading?: boolean;
	/**
	 * The callback action when a user presses the component.
	 */
	onPress?: () => void;
	/**
	 * The placeholder to be displayed on the far right of the component, displayed when no value is passed.
	 */
	placeholder?: string;
	/**
	 * Determines whether the border for items should be hidden. False by default.
	 */
	showBorder?: boolean;
	/**
	 * The value to be displayed on the far right of the component, replaces placeholder.
	 */
	value?: string | React.ReactNode;
	/**
	 * An object to style the outside container of the component.
	 */
	style?: ViewProps['style'];
}

export const WBItem: React.FC<ItemProps> = ({
	chevron = false,
	children,
	icon,
	isDisabled = false,
	label = '',
	loading = false,
	onPress,
	placeholder = '',
	showBorder = true,
	value = '',
	style,
}) => {
	const touchableProps = { disabled: isDisabled, onPress };

	const styles = getModifiedStyles(itemStyles, { isDisabled });

	return (
		<Pressable activeOpacity={0.6} {...touchableProps}>
			<View style={[styles.item, style]}>
				{!!icon && (
					<View style={[styles.icon, styles.iconMain]}>
						<WBIcon color={styles.icon.color as string} name={icon} />
					</View>
				)}
				<View style={[styles.wrapper, showBorder && styles.wrapperBorder]}>
					<View style={styles.header}>
						<View style={styles.content}>
							<View style={[styles.side, styles.label]}>
								{typeof label === 'string' ? <WBText numberOfLines={1}>{label}</WBText> : label}
							</View>
							<View style={styles.side}>
								{loading ? (
									<View style={styles.activityIndicator}>
										<ActivityIndicator />
									</View>
								) : value ? (
									typeof value === 'string' ? (
										<WBText numberOfLines={1} style={styles.value}>
											{`  ${value}`}
										</WBText>
									) : (
										value
									)
								) : (
									<WBText numberOfLines={1} style={[styles.value, styles.placeholder]}>
										{placeholder}
									</WBText>
								)}
							</View>
						</View>
						{!!chevron && (
							<View style={styles.icon}>
								<WBIcon name='chevron-right' color={StyleSheet.value('$inputChevronColor')} size={15} />
							</View>
						)}
					</View>
					{children}
				</View>
			</View>
		</Pressable>
	);
};
